import React from "react";

import BrandWordmark from "../components/BrandWordmark";
import { PLATFORM_NAME } from "../brand";
import "./About.css";

/**
 * Public "About" — the platform, not the product.
 *
 * It used to be about GoldBot, because GoldBot was all there was. OMJ VISION
 * is the parent now and GoldBot is the first thing built inside it, so this
 * page has to describe the shape of the whole and be honest that the shape is
 * currently one product deep.
 *
 * Deliberately short on adjectives. No company size, no customer count, no
 * returns, no regulatory standing, no claims about the technology beyond what
 * the product visibly does — none of which could be substantiated, all of
 * which would be the first thing a reader stopped believing. What it claims
 * instead is a method, which is checkable: every figure in the product is
 * read from the running system, and the product says so when it cannot read
 * one.
 */
function About() {
  return (
    <main className="about">
      <section className="about-hero">
        <p className="about-eyebrow">About</p>
        <h1>
          <BrandWordmark size="lg" as="span" />
        </h1>
        <p className="about-lead">
          {PLATFORM_NAME} builds intelligent systems where technology,
          automation, data and disciplined execution work as one — software
          that makes a decision the same way every time, and shows its work.
        </p>
      </section>

      <section className="about-section">
        <h2>The idea</h2>
        <p>
          Most systems fail not because the thinking was wrong but because the
          doing drifted: a rule applied on Monday and skipped on Friday, a
          number nobody could trace, a result nobody could reproduce. The
          discipline a good process needs is exactly the discipline software is
          best at.
        </p>
        <p>
          So we build the process into the system. A strategy becomes code,
          the code runs continuously, and every decision it takes is recorded
          where it can be read back. What that removes is not judgement — it is
          the gap between what was decided and what was done.
        </p>
      </section>

      <section className="about-section">
        <h2>How we build</h2>
        <dl className="about-principles">
          <div>
            <dt>Automated, not unattended</dt>
            <dd>
              A system runs on its own and reports what it did. Those are two
              requirements, and the second is the one usually missing.
            </dd>
          </div>
          <div>
            <dt>Measured, not asserted</dt>
            <dd>
              Every figure is read from the live system. When a value cannot be
              read, the interface says so and shows a dash rather than filling
              the gap.
            </dd>
          </div>
          <div>
            <dt>Access is granted, not assumed</dt>
            <dd>
              What an account can open is decided server-side, per account, and
              re-checked on every request.
            </dd>
          </div>
        </dl>
      </section>

      <section className="about-section about-product">
        <h2>GoldBot</h2>
        <p className="about-product__tag">The first system in the ecosystem</p>
        <p>
          GoldBot is an automated trading system for XAUUSD, and the control
          center that reports what it is doing: current status and uptime, open
          positions, which strategies are active, a live event log, and
          recorded performance from closed trades.
        </p>
        <p>
          It is the first product built on {PLATFORM_NAME}, and the one the
          platform's ideas were worked out on. It is not the last — but what
          comes next will be announced when it exists, not before.
        </p>
      </section>

      <section className="about-section">
        <h2>Open to everyone</h2>
        <p>
          The market pages — live crypto and gold prices, and a currency
          converter — are public and need no account. The product surfaces are
          invite-only while the platform is in development.
        </p>
      </section>

      <p className="about-note">
        GoldBot is trading software, not investment advice. Automated trading
        carries risk, including loss of capital.
      </p>
    </main>
  );
}

export default About;
